import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Clock, Tag } from 'lucide-react';

const Blog = () => {
  const blogPosts = [
    {
      id: 1,
      title: '10 Web Development Trends Shaping 2025',
      excerpt: 'Discover the latest trends in web development that are revolutionizing how we build digital experiences.',
      image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&h=600&fit=crop',
      author: 'Sarah Chen',
      date: 'January 15, 2025',
      category: 'Web Development',
      readTime: '8 min read',
      slug: 'web-development-trends-2025'
    },
    {
      id: 2,
      title: 'The Future of AI in Business Applications',
      excerpt: 'How artificial intelligence is transforming business operations and creating new opportunities.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=600&fit=crop',
      author: 'Marcus Rodriguez',
      date: 'January 12, 2025',
      category: 'AI & ML',
      readTime: '10 min read',
      slug: 'ai-business-applications'
    },
    {
      id: 3,
      title: 'UI/UX Design Principles for Modern Apps',
      excerpt: 'Essential design principles that create intuitive and engaging user experiences.',
      image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&h=600&fit=crop',
      author: 'Emily Watson',
      date: 'January 10, 2025',
      category: 'Design',
      readTime: '6 min read',
      slug: 'ux-design-principles'
    },
    {
      id: 4,
      title: 'Cloud Migration: A Complete Guide',
      excerpt: 'Everything you need to know about migrating your infrastructure to the cloud.',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop',
      author: 'David Park',
      date: 'January 8, 2025',
      category: 'Cloud Solutions',
      readTime: '12 min read',
      slug: 'cloud-migration-guide'
    },
    {
      id: 5,
      title: 'E-Commerce Optimization Strategies',
      excerpt: 'Proven strategies to boost your online store\'s conversion rates and sales.',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop',
      author: 'Sarah Chen',
      date: 'January 5, 2025',
      category: 'E-Commerce',
      readTime: '7 min read',
      slug: 'ecommerce-optimization'
    },
    {
      id: 6,
      title: 'Building Scalable Mobile Apps',
      excerpt: 'Best practices for developing mobile applications that scale with your business.',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop',
      author: 'Marcus Rodriguez',
      date: 'January 3, 2025',
      category: 'Mobile Development',
      readTime: '9 min read',
      slug: 'scalable-mobile-apps'
    }
  ];

  const categories = ['All', 'Web Development', 'AI & ML', 'Design', 'Cloud Solutions', 'E-Commerce', 'Mobile Development'];
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="bg-white pt-16">
      {/* Hero Section */}
      <section className="relative py-32 px-8 lg:px-16 overflow-hidden bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white leading-tight tracking-tight" style={{ fontWeight: 700 }}>
              SYNCORE LLC
              <br />
              <span className="bg-gradient-to-r from-light-pink to-light-blue bg-clip-text text-transparent">
                Blog & Insights
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed">
              Expert insights, industry trends, and practical guides from our team of digital innovators
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-8 lg:px-16 bg-grey sticky top-20 z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-xl font-semibold text-sm uppercase tracking-wider transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-light-pink to-mid-purple text-white shadow-lg'
                    : 'bg-white text-dark-grey hover:bg-light-pink hover:text-white border border-grey'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-24 px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white rounded-2xl overflow-hidden border border-grey hover:border-light-pink hover:shadow-2xl transition-all"
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-4 py-2 rounded-full bg-light-pink text-white text-xs font-bold uppercase tracking-wider">
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Meta */}
                  <div className="flex items-center gap-4 mb-4 text-sm text-dark-grey">
                    <div className="flex items-center gap-2">
                      <Calendar size={16} />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={16} />
                      <span>{post.readTime}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold mb-3 group-hover:text-light-pink transition-colors">
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-dark-grey mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>

                  {/* Author & CTA */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <User size={16} className="text-mid-purple" />
                      <span className="font-semibold">{post.author}</span>
                    </div>
                    <Link to={`/blog/${post.slug}`}>
                      <motion.button
                        whileHover={{ x: 5 }}
                        className="flex items-center gap-2 text-mid-purple font-semibold text-sm"
                      >
                        Read More
                        <ArrowRight size={16} />
                      </motion.button>
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-24 px-8 lg:px-16 bg-gradient-to-br from-[#0a0015] via-[#1a0a2e] to-[#0f0a1a]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white tracking-tight" style={{ fontWeight: 700 }}>
              Subscribe to Our Newsletter
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Get the latest insights delivered directly to your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-xl bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-light-pink"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 rounded-xl bg-gradient-to-r from-light-pink to-mid-purple text-white font-bold uppercase tracking-wider hover:shadow-2xl transition-all"
              >
                Subscribe
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
